<template>
  <b-modal
    v-model="show"
    size="lg"
    hide-footer
    :no-close-on-backdrop="terminalChargeInFlight || sendingInProgress"
    :no-close-on-esc="terminalChargeInFlight || sendingInProgress"
    :title="modalTitle"
    @hidden="onHidden"
  >
    <!-- ──────────────────────────────────────────────────────────────── -->
    <!-- STEP 1 — SETUP: amount + method picker                            -->
    <!-- ──────────────────────────────────────────────────────────────── -->
    <div v-if="step === 'setup'" class="cp-setup">
      <div class="cp-balance-row">
        <div class="cp-balance-label">Patient balance</div>
        <div class="cp-balance-value" :class="{ 'cp-balance-zero': !hasBalance }">
          {{ formatMoney(patientBalance) }}
        </div>
      </div>

      <div class="cp-purpose-block">
        <label class="cp-label">What is this payment for?</label>
        <div class="cp-purpose-grid">
          <button
            type="button"
            class="cp-purpose-card"
            :class="{ active: purpose === 'balance' }"
            @click="purpose = 'balance'"
          >
            <div class="cp-purpose-title">Outstanding balance</div>
            <div class="cp-purpose-sub">Collect toward what the patient owes</div>
          </button>

          <button
            type="button"
            class="cp-purpose-card"
            :class="{ active: purpose === 'deposit', disabled: !depositAvailable }"
            :disabled="!depositAvailable"
            @click="purpose = 'deposit'"
          >
            <div class="cp-purpose-title">Treatment plan deposit</div>
            <div class="cp-purpose-sub">Posts to Open Dental as a prepayment</div>
            <div v-if="!depositAvailable" class="cp-purpose-disabled-reason">
              {{ depositDisabledReason }}
            </div>
          </button>
        </div>
      </div>

      <div class="cp-amount-block">
        <label class="cp-label">Amount to collect</label>
        <b-input-group prepend="$" class="cp-amount-input-group">
          <b-form-input
            type="number"
            v-model.number="amount"
            :min="0.01"
            :max="maxAmount"
            step="0.01"
            placeholder="0.00"
            ref="amountInput"
          />
        </b-input-group>
        <p class="cp-help-text">
          <span v-if="isDepositPurpose">
            Enter the deposit amount. It posts to Open Dental as an unearned
            prepayment credit on the patient's account.
          </span>
          <span v-else>
            Default: full balance. Lower this to collect a partial payment.
          </span>
          <span v-if="amountExceedsCap" class="text-danger d-block">
            Amount cannot exceed {{ formatMoney(maxAmount) }}.
          </span>
          <span v-else-if="!amountValid" class="text-danger d-block">
            Enter an amount greater than zero.
          </span>
        </p>

        <div v-if="isDepositPurpose && signedPlanOptions.length > 1" class="cp-plan-select">
          <label class="cp-label">Apply to signed treatment plan (optional)</label>
          <b-form-select v-model="selectedTreatPlanNum" :options="signedPlanOptions" />
        </div>
      </div>

      <hr />

      <h6 class="cp-section-title">How would you like to collect this payment?</h6>

      <div class="cp-method-grid">
        <button
          type="button"
          class="cp-method-card"
          :class="{ active: selectedMethod === 'sms', disabled: !canSms }"
          :disabled="!canSms"
          @click="selectedMethod = 'sms'"
        >
          <div class="cp-method-icon"><i class="fa fa-mobile" /></div>
          <div class="cp-method-title">Send SMS</div>
          <div class="cp-method-sub">Patient pays from their phone</div>
          <div v-if="!canSms" class="cp-method-disabled-reason">{{ smsDisabledReason }}</div>
        </button>

        <button
          type="button"
          class="cp-method-card"
          :class="{ active: selectedMethod === 'email', disabled: !canEmail }"
          :disabled="!canEmail"
          @click="selectedMethod = 'email'"
        >
          <div class="cp-method-icon"><i class="fa fa-envelope" /></div>
          <div class="cp-method-title">Send Email</div>
          <div class="cp-method-sub">Patient pays from their inbox</div>
          <div v-if="!canEmail" class="cp-method-disabled-reason">{{ emailDisabledReason }}</div>
        </button>

        <button
          type="button"
          class="cp-method-card"
          :class="{ active: selectedMethod === 'both', disabled: !canSms || !canEmail }"
          :disabled="!canSms || !canEmail"
          @click="selectedMethod = 'both'"
        >
          <div class="cp-method-icon"><i class="fa fa-paper-plane" /></div>
          <div class="cp-method-title">SMS + Email</div>
          <div class="cp-method-sub">Send to both at once</div>
          <div v-if="!canSms || !canEmail" class="cp-method-disabled-reason">
            {{ !canSms ? smsDisabledReason : emailDisabledReason }}
          </div>
        </button>

        <button
          type="button"
          class="cp-method-card"
          :class="{ active: selectedMethod === 'terminal', disabled: !canTerminal }"
          :disabled="!canTerminal"
          @click="selectedMethod = 'terminal'"
        >
          <div class="cp-method-icon"><i class="fa fa-credit-card" /></div>
          <div class="cp-method-title">Charge on Terminal</div>
          <div class="cp-method-sub">Patient swipes a card here</div>
          <div v-if="!canTerminal" class="cp-method-disabled-reason">{{ terminalDisabledReason }}</div>
        </button>
      </div>

      <div class="cp-actions">
        <b-button
          variant="success"
          :disabled="!canContinue"
          @click="onContinue"
        >
          Continue <i class="fa fa-arrow-right ml-1" />
        </b-button>
        <b-button variant="outline-secondary" @click="closeModal">Cancel</b-button>
      </div>
    </div>

    <!-- ──────────────────────────────────────────────────────────────── -->
    <!-- STEP 2A — SMS configure                                           -->
    <!-- ──────────────────────────────────────────────────────────────── -->
    <div v-else-if="step === 'sms-configure'" class="cp-configure">
      <div class="cp-summary-row">
        <span>
          <strong>{{ formatMoney(amount) }}</strong> via <strong>SMS</strong>
          <span v-if="isDepositPurpose" class="cp-purpose-badge">{{ purposeLabel }}</span>
        </span>
        <span class="text-muted">to {{ patientPhoneFormatted }}</span>
      </div>

      <div class="cp-section">
        <div class="cp-section-header">
          <i class="fa fa-mobile cp-section-icon" />
          <span class="cp-section-title-text">SMS template</span>
        </div>
        <div class="cp-section-body">
          <b-form-select v-model="selectedSmsTemplateID" @change="onSmsTemplatePicked">
            <option :value="null" disabled>Select a template…</option>
            <option v-for="t in paymentSmsTemplates" :key="t.WikiPageID" :value="t.WikiPageID">
              {{ t.Name }}
            </option>
          </b-form-select>
          <p v-if="paymentSmsTemplates.length === 0" class="cp-help-text mb-0 mt-2">
            No payment SMS templates found. Tag a wiki page as "payment" in Wiki to create one.
          </p>

          <label class="cp-label mt-3">SMS text</label>
          <textarea
            v-model="smsText"
            class="form-control"
            rows="6"
            placeholder="Pick a template above to populate, or write your own…"
          ></textarea>
          <!-- Shared SMS guidance: live credit counter + warn/block banner + tips (template-aware: {{payment_link}} etc.) -->
          <SmsGuidance :text="smsText || ''" substitute-tags @change="onSmsGuidance" />
          <p class="cp-help-text">
            Variables like <code v-pre>{{payment_link}}</code> are substituted server-side at send time.
          </p>
        </div>
      </div>

      <b-alert v-if="smsOutboundOff" variant="warning" show class="cp-phone-problem" data-test="cp-sms-outbound-off">
        <i class="fa fa-ban mr-1" />Texting is turned off for this office. Send the payment link by email instead.
      </b-alert>

      <b-alert v-if="smsOptOutBlock.blocked" variant="warning" show class="cp-phone-problem">
        <i class="fa fa-user-times mr-1" />{{ smsOptOutBlock.message }}
      </b-alert>

      <b-alert v-if="phoneProblem" variant="warning" show class="cp-phone-problem">
        <i class="fa fa-phone mr-1" />{{ phoneProblem }}
      </b-alert>

      <div class="cp-actions">
        <b-button variant="success" :disabled="!canSendSms" @click="sendSmsOnly">
          <b-spinner v-if="sendingInProgress" small class="mr-1" />
          <i v-else class="fa fa-mobile mr-1" /> Send Payment SMS
        </b-button>
        <b-button variant="outline-secondary" :disabled="sendingInProgress" @click="goBackToSetup">
          <i class="fa fa-arrow-left mr-1" /> Back
        </b-button>
      </div>
    </div>

    <!-- ──────────────────────────────────────────────────────────────── -->
    <!-- STEP 2B — EMAIL configure                                         -->
    <!-- ──────────────────────────────────────────────────────────────── -->
    <div v-else-if="step === 'email-configure'" class="cp-configure">
      <div class="cp-summary-row">
        <span>
          <strong>{{ formatMoney(amount) }}</strong> via <strong>Email</strong>
          <span v-if="isDepositPurpose" class="cp-purpose-badge">{{ purposeLabel }}</span>
        </span>
        <span class="text-muted">to {{ patientEmail || '(no email)' }}</span>
      </div>

      <div class="cp-section">
        <div class="cp-section-header">
          <i class="fa fa-envelope cp-section-icon" />
          <span class="cp-section-title-text">Email template</span>
        </div>
        <div class="cp-section-body">
          <b-form-select v-model="selectedEmailTemplateID">
            <option :value="null" disabled>Select a template…</option>
            <option v-for="t in paymentEmailTemplates" :key="t.WikiPageID" :value="t.WikiPageID">
              {{ t.Name }}
            </option>
          </b-form-select>
          <p v-if="paymentEmailTemplates.length === 0" class="cp-help-text mb-0 mt-2">
            No payment email templates found. Tag a wiki page as both "payment" and "email-template" in Wiki.
          </p>
          <p v-else class="cp-help-text mb-0 mt-2">
            The email body comes from the wiki template. <code v-pre>{{payment_link}}</code> is substituted
            server-side at send time.
          </p>
        </div>
      </div>

      <div class="cp-actions">
        <b-button variant="success" :disabled="!canSendEmail" @click="sendEmailOnly">
          <b-spinner v-if="sendingInProgress" small class="mr-1" />
          <i v-else class="fa fa-envelope mr-1" /> Send Payment Email
        </b-button>
        <b-button variant="outline-secondary" :disabled="sendingInProgress" @click="goBackToSetup">
          <i class="fa fa-arrow-left mr-1" /> Back
        </b-button>
      </div>
    </div>

    <!-- ──────────────────────────────────────────────────────────────── -->
    <!-- STEP 2C — BOTH configure                                          -->
    <!-- ──────────────────────────────────────────────────────────────── -->
    <div v-else-if="step === 'both-configure'" class="cp-configure">
      <div class="cp-summary-row">
        <span>
          <strong>{{ formatMoney(amount) }}</strong> via <strong>SMS + Email</strong>
          <span v-if="isDepositPurpose" class="cp-purpose-badge">{{ purposeLabel }}</span>
        </span>
      </div>

      <div class="cp-section">
        <div class="cp-section-header">
          <i class="fa fa-mobile cp-section-icon" />
          <span class="cp-section-title-text">SMS</span>
          <span class="cp-section-action text-muted small">to {{ patientPhoneFormatted }}</span>
        </div>
        <div class="cp-section-body">
          <b-form-select v-model="selectedSmsTemplateID" @change="onSmsTemplatePicked">
            <option :value="null" disabled>Select a template…</option>
            <option v-for="t in paymentSmsTemplates" :key="t.WikiPageID" :value="t.WikiPageID">
              {{ t.Name }}
            </option>
          </b-form-select>

          <label class="cp-label mt-3">SMS text</label>
          <textarea v-model="smsText" class="form-control" rows="4"></textarea>
          <!-- Shared SMS guidance: live credit counter + warn/block banner + tips (template-aware) -->
          <SmsGuidance :text="smsText || ''" substitute-tags @change="onSmsGuidance" />
        </div>
      </div>

      <div class="cp-section">
        <div class="cp-section-header">
          <i class="fa fa-envelope cp-section-icon" />
          <span class="cp-section-title-text">Email</span>
          <span class="cp-section-action text-muted small">to {{ patientEmail || '(no email)' }}</span>
        </div>
        <div class="cp-section-body">
          <b-form-select v-model="selectedEmailTemplateID">
            <option :value="null" disabled>Select a template…</option>
            <option v-for="t in paymentEmailTemplates" :key="t.WikiPageID" :value="t.WikiPageID">
              {{ t.Name }}
            </option>
          </b-form-select>
        </div>
      </div>

      <div class="cp-actions">
        <b-button variant="success" :disabled="!canSendBoth" @click="sendBoth">
          <b-spinner v-if="sendingInProgress" small class="mr-1" />
          <i v-else class="fa fa-paper-plane mr-1" /> Send SMS &amp; Email
        </b-button>
        <b-button variant="outline-secondary" :disabled="sendingInProgress" @click="goBackToSetup">
          <i class="fa fa-arrow-left mr-1" /> Back
        </b-button>
      </div>
    </div>

    <!-- ──────────────────────────────────────────────────────────────── -->
    <!-- STEP 2D — TERMINAL configure                                       -->
    <!-- ──────────────────────────────────────────────────────────────── -->
    <div v-else-if="step === 'terminal-configure'" class="cp-configure">
      <div class="cp-summary-row">
        <span>
          <strong>{{ formatMoney(amount) }}</strong> via <strong>Terminal</strong>
          <span v-if="isDepositPurpose" class="cp-purpose-badge">{{ purposeLabel }}</span>
        </span>
      </div>

      <!-- Terminal picker card -->
      <div class="cp-section">
        <div class="cp-section-header">
          <i class="fa fa-credit-card cp-section-icon" />
          <span class="cp-section-title-text">Select terminal</span>
          <b-button
            v-if="!terminalDevicesLoading && terminalDevices.length > 0"
            variant="link"
            size="sm"
            class="cp-section-action"
            @click="fetchTerminalDevices"
          >
            <i class="fa fa-refresh mr-1" /> Refresh
          </b-button>
        </div>

        <div class="cp-section-body">
          <p v-if="terminalDevicesLoading" class="cp-help-text mb-0">
            <i class="fa fa-spinner fa-spin mr-1" /> Loading terminals…
          </p>
          <div v-else-if="terminalDevicesError" class="cp-empty-state cp-empty-error">
            <i class="fa fa-exclamation-triangle cp-empty-icon" />
            <div class="cp-empty-title">Couldn't load terminals</div>
            <div class="cp-empty-sub">{{ terminalDevicesError }}</div>
            <b-button variant="outline-primary" size="sm" class="mt-2" @click="fetchTerminalDevices">
              <i class="fa fa-refresh mr-1" /> Try again
            </b-button>
          </div>
          <div v-else-if="terminalDevices.length === 0" class="cp-empty-state">
            <i class="fa fa-credit-card cp-empty-icon" />
            <div class="cp-empty-title">No active terminals</div>
            <div class="cp-empty-sub">
              No <strong>ACTIVATED</strong> terminals are registered with Moolah for this site.
              Activate one in your Moolah portal, or pick a different payment method.
            </div>
            <div class="cp-empty-actions">
              <b-button variant="outline-primary" size="sm" @click="fetchTerminalDevices">
                <i class="fa fa-refresh mr-1" /> Refresh
              </b-button>
              <b-button variant="outline-secondary" size="sm" @click="goBackToSetup">
                <i class="fa fa-arrow-left mr-1" /> Pick another method
              </b-button>
            </div>
          </div>
          <div v-else class="cp-terminal-list">
            <button
              v-for="d in terminalDevices"
              :key="d.id"
              type="button"
              class="cp-terminal-card"
              :class="{ active: selectedTerminalId === d.id, virtual: d.isVirtual }"
              @click="selectedTerminalId = d.id"
            >
              <div class="cp-terminal-radio">
                <i :class="selectedTerminalId === d.id ? 'fa fa-dot-circle-o' : 'fa fa-circle-o'" />
              </div>
              <div class="cp-terminal-info">
                <div class="cp-terminal-name">
                  {{ d.name }}
                  <span v-if="d.isVirtual" class="cp-terminal-badge" title="Virtual Terminal — software-only, intended for testing">
                    <i class="fa fa-flask mr-1" />Virtual — test only
                  </span>
                </div>
                <div v-if="d.serialNumber" class="cp-terminal-serial">SN: {{ d.serialNumber }}</div>
              </div>
            </button>
          </div>
        </div>
      </div>

      <!-- Receipt options card -->
      <div v-if="terminalDevices.length > 0" class="cp-section">
        <div class="cp-section-header">
          <i class="fa fa-receipt cp-section-icon" />
          <span class="cp-section-title-text">Receipt options</span>
        </div>

        <div class="cp-section-body">
          <label class="cp-toggle-row" :class="{ active: receiptShowOnTerminal }">
            <input type="checkbox" v-model="receiptShowOnTerminal" class="cp-toggle-input" />
            <span class="cp-toggle-icon">
              <i :class="receiptShowOnTerminal ? 'fa fa-check-square' : 'fa fa-square-o'" />
            </span>
            <span class="cp-toggle-content">
              <span class="cp-toggle-title">Show receipt on terminal screen</span>
              <span class="cp-toggle-sub">Patient sees the amount + tap-to-pay prompts on the device</span>
            </span>
          </label>

          <label class="cp-toggle-row" :class="{ active: receiptEmail }">
            <input type="checkbox" v-model="receiptEmail" class="cp-toggle-input" />
            <span class="cp-toggle-icon">
              <i :class="receiptEmail ? 'fa fa-check-square' : 'fa fa-square-o'" />
            </span>
            <span class="cp-toggle-content">
              <span class="cp-toggle-title">Email receipt to patient</span>
              <span class="cp-toggle-sub">Send a Moolah-generated receipt by email</span>
            </span>
          </label>

          <div v-if="receiptEmail" class="cp-receipt-email-block">
            <p v-if="!patientEmail" class="cp-warning-row">
              <i class="fa fa-exclamation-triangle mr-1" />
              No email on file — enter one below if they want a receipt.
            </p>
            <input
              v-model="receiptEmailAddress"
              type="email"
              class="form-control"
              placeholder="patient@example.com"
            />
          </div>
        </div>
      </div>

      <div class="cp-actions">
        <b-button
          variant="success"
          :disabled="!canSendToTerminal"
          @click="sendToTerminal"
        >
          <i class="fa fa-credit-card mr-1" /> Send to Terminal
        </b-button>
        <b-button variant="outline-secondary" @click="goBackToSetup">
          <i class="fa fa-arrow-left mr-1" /> Back
        </b-button>
      </div>
    </div>

    <!-- ──────────────────────────────────────────────────────────────── -->
    <!-- STEP 3 — TERMINAL state machine                                    -->
    <!-- ──────────────────────────────────────────────────────────────── -->
    <div v-else-if="step === 'terminal-state'" class="cp-state-block">

      <template v-if="terminalChargeState === 'sending' || terminalChargeState === 'waiting' || terminalChargeState === 'polling'">
        <div class="cp-state-icon spin"><i class="fa fa-spinner fa-spin" /></div>
        <h5 v-if="terminalChargeState === 'sending'">Sending to terminal…</h5>
        <h5 v-else-if="terminalChargeState === 'waiting'">
          Waiting for card on <strong>{{ selectedTerminalName }}</strong>…
        </h5>
        <h5 v-else>{{ terminalChargeMessage || 'Checking status…' }}</h5>
        <p class="cp-help-text">
          The card swipe can take up to 90 seconds. Press <strong>Cancel</strong>
          on the terminal device to also abort there.
        </p>
        <div class="cp-actions cp-actions-center">
          <b-button
            variant="outline-primary"
            :disabled="checkingStatus || !terminalPaymentID"
            @click="manualCheckStatus"
            title="Re-fetch live status from Moolah"
          >
            <b-spinner v-if="checkingStatus" small class="mr-1" />
            <i v-else class="fa fa-refresh mr-1" />
            Check status
          </b-button>
          <b-button
            variant="outline-danger"
            :disabled="cancellingCharge"
            @click="cancelInFlightCharge"
          >
            <b-spinner v-if="cancellingCharge" small class="mr-1" />
            <i v-else class="fa fa-ban mr-1" />
            Cancel charge
          </b-button>
        </div>
      </template>

      <template v-else-if="terminalChargeState === 'success'">
        <div class="cp-state-icon ok"><i class="fa fa-check" /></div>
        <h5>Payment captured</h5>
        <!-- The amount the CARD gave, not the amount typed. They're the same on
             a normal charge; on a partial approval showing the typed amount here
             is what let a $117.86 shortfall go unnoticed (Tulip Tree 8/18). -->
        <p class="cp-state-amount">{{ formatMoney(collectedAmount) }}</p>
        <p v-if="isDepositPurpose" class="cp-help-text">
          Recorded in Open Dental as a treatment plan prepayment.
        </p>
        <p v-if="terminalChargeTransactionId" class="cp-help-text">
          Txn: {{ terminalChargeTransactionId }}
        </p>
        <div class="cp-actions">
          <b-button variant="success" @click="closeModal">Done</b-button>
        </div>
      </template>

      <!-- Partially approved: the card had less on it than we asked for. The
           processor approved what was available and Moolah reports it as an
           ordinary success, so this state has to shout — the patient is still
           standing at the desk and the remainder is collectable right now. -->
      <template v-else-if="terminalChargeState === 'partial'">
        <div class="cp-state-icon warn"><i class="fa fa-exclamation-triangle" /></div>
        <h5>Partially approved</h5>
        <p class="cp-state-amount">{{ formatMoney(terminalCapturedAmount) }}</p>
        <div class="cp-partial-breakdown">
          <div class="cp-partial-row">
            <span>Requested</span><span>{{ formatMoney(terminalRequestedAmount) }}</span>
          </div>
          <div class="cp-partial-row">
            <span>Approved on this card</span><span>{{ formatMoney(terminalCapturedAmount) }}</span>
          </div>
          <div class="cp-partial-row cp-partial-row--due">
            <span>Not collected</span><span>{{ formatMoney(terminalShortfall) }}</span>
          </div>
        </div>
        <p class="cp-help-text">
          The card only had {{ formatMoney(terminalCapturedAmount) }} available — common with
          HSA, FSA and prepaid cards. Open Dental was posted for the approved amount,
          so the remaining {{ formatMoney(terminalShortfall) }} is still owed.
        </p>
        <p v-if="terminalChargeTransactionId" class="cp-help-text">
          Txn: {{ terminalChargeTransactionId }}
        </p>
        <div class="cp-actions">
          <b-button variant="success" :disabled="chargingRemainder" @click="chargeRemainder">
            <b-spinner v-if="chargingRemainder" small class="mr-1" />
            <i v-else class="fa fa-credit-card mr-1" />
            Charge remaining {{ formatMoney(terminalShortfall) }}
          </b-button>
          <b-button variant="outline-secondary" @click="closeModal">Done for now</b-button>
        </div>
      </template>

      <template v-else-if="terminalChargeState === 'declined'">
        <div class="cp-state-icon bad"><i class="fa fa-times" /></div>
        <h5>Card declined</h5>
        <p class="cp-help-text">{{ terminalChargeMessage || 'The card was declined.' }}</p>
        <div class="cp-actions">
          <b-button variant="success" @click="resetTerminalToConfigure">
            <i class="fa fa-refresh mr-1" /> Try again
          </b-button>
          <b-button variant="outline-secondary" @click="closeModal">Cancel</b-button>
        </div>
      </template>

      <template v-else-if="terminalChargeState === 'cancelled'">
        <div class="cp-state-icon mute"><i class="fa fa-ban" /></div>
        <h5>{{ cancelledByOperator ? 'Charge cancelled' : 'Cancelled at terminal' }}</h5>
        <p class="cp-help-text">
          <template v-if="cancelledByOperator">
            We've stopped tracking this charge.
            <strong>Press Cancel on the terminal device</strong> to abort the swipe there too —
            otherwise the patient could still complete it. Verify in Moolah reports.
          </template>
          <template v-else>
            {{ terminalChargeMessage || 'The transaction was cancelled.' }}
          </template>
        </p>
        <div class="cp-actions cp-actions-center">
          <b-button variant="success" @click="resetTerminalToConfigure">
            <i class="fa fa-refresh mr-1" /> Try again
          </b-button>
          <b-button variant="outline-secondary" @click="closeModal">Done</b-button>
        </div>
      </template>

      <template v-else-if="terminalChargeState === 'error'">
        <div class="cp-state-icon warn"><i class="fa fa-exclamation-triangle" /></div>
        <h5>Something went wrong</h5>
        <p class="cp-help-text">{{ terminalChargeMessage || 'Failed to complete the charge.' }}</p>
        <div class="cp-actions">
          <b-button variant="success" @click="resetTerminalToConfigure">
            <i class="fa fa-refresh mr-1" /> Try again
          </b-button>
          <b-button variant="outline-secondary" @click="closeModal">Cancel</b-button>
        </div>
      </template>

      <template v-else-if="terminalChargeState === 'blocked'">
        <div class="cp-state-icon warn"><i class="fa fa-hand-paper-o" /></div>
        <h5>Charge not sent</h5>
        <p class="cp-help-text">{{ terminalChargeMessage }}</p>
        <p class="cp-help-text">
          Nothing reached the terminal, so <strong>this attempt did not charge any card</strong>.
        </p>
        <div class="cp-actions cp-actions-center">
          <!-- "Done" is primary on purpose: retrying right away just gets refused
               again while the other charge is still live. -->
          <b-button variant="success" @click="closeModal">Done</b-button>
          <b-button variant="outline-secondary" @click="resetTerminalToConfigure">
            <i class="fa fa-refresh mr-1" /> Try again
          </b-button>
        </div>
      </template>

      <template v-else-if="terminalChargeState === 'unconfirmed'">
        <div class="cp-state-icon warn"><i class="fa fa-question-circle" /></div>
        <h5>Charge not confirmed</h5>
        <p class="cp-help-text">{{ terminalChargeMessage || 'We could not confirm this charge.' }}</p>
        <p class="cp-help-text">
          <strong>Do not re-charge this patient</strong> unless you have confirmed in Moolah that the
          card was not charged. A card swiped after the wait can still go through, and it will post
          here automatically if it did.
        </p>
        <p class="cp-help-text">
          This charge keeps the terminal reserved for up to an hour. If you've confirmed the card was
          <strong>not</strong> charged, press <strong>Cancel charge</strong> to free it for the next patient.
        </p>
        <div class="cp-actions cp-actions-center">
          <b-button
            variant="outline-primary"
            :disabled="checkingStatus || !terminalPaymentID"
            @click="manualCheckStatus"
            title="Re-fetch live status from Moolah"
          >
            <b-spinner v-if="checkingStatus" small class="mr-1" />
            <i v-else class="fa fa-refresh mr-1" />
            Check status
          </b-button>
          <b-button
            variant="outline-danger"
            :disabled="cancellingCharge || !terminalPaymentID"
            @click="cancelInFlightCharge(true)"
          >
            <b-spinner v-if="cancellingCharge" small class="mr-1" />
            <i v-else class="fa fa-ban mr-1" />
            Cancel charge
          </b-button>
          <b-button variant="outline-secondary" @click="closeModal">Done</b-button>
        </div>
      </template>

    </div>

  </b-modal>
</template>

<script>
import { mapActions } from 'vuex';
import SmsGuidance from '../../../components/SmsGuidance.vue';
import { classifyTerminalChargeError, terminalChargeErrorMessage, TERMINAL_CHARGE_OUTCOME } from '../../../utils/terminalChargeError';
import { extractApiError } from '../../../utils/apiError';
import { PAYMENT_PURPOSE, resolveCollectPaymentDefaults, buildDepositIntentFields } from '../../../utils/collectPaymentPurpose';
import { isTextablePhone, describePhoneProblem } from '../../../utils/patientPhone';
import { getTextBlockReason, optOutResultFromSendFailure } from '../../../utils/textConsent';
import { hasSmsNumber, isSmsOutboundDisabled } from '../../../utils/siteCapability';
import { paymentSmsTemplatesFrom, paymentEmailTemplatesFrom } from '../../../utils/wikiTags';

export default {
  name: 'CollectPaymentModal',

  components: { SmsGuidance },

  props: {
    value: { type: Boolean, required: true },
    siteId: { type: String, required: true },
    patNum: { type: [Number, String], required: true },
    patientData: { type: Object, required: true },
    patientBalance: { type: Number, default: 0 },
    user: { type: Object, required: true },
    // Site object — parent computes via the mixin's getSite() and passes
    // through. The modal only needs PaymentProvider + StripeAccountId from
    // it; passing the whole object keeps the prop list short and matches
    // the shape getSite returns.
    site: { type: Object, default: null },
    // Resolved TEXT_TO_PAY_SETTINGS_PAYMENT for this site. Only
    // `prepaymentUnearnedType` is read here — it decides whether a treatment
    // plan deposit can be offered at all. Parent fetches it per open and
    // passes {} on failure, same degrade TxPresenterPresentMode uses.
    textToPaySettings: { type: Object, default: () => ({}) },
    // The patient's Tx-Presenter-signed plans, already loaded by the profile
    // for its "Treatment Plans Signed" table. Used only to let the operator
    // attach a deposit to a plan; empty array simply hides the dropdown.
    signedPlans: { type: Array, default: () => [] },
    // Site timezone (GENERAL_TIMEZONE) — passed to the shared text-consent rule so a
    // texted-opt-out date renders in the practice's zone. Optional; degrades to UTC.
    timezone: { type: String, default: '' },
    // Optional { amount, purpose: 'balance'|'deposit', treatPlanNum } — set when the dialog is opened
    // from an Action Center "Payment failed → Send new link" card (src/utils/actionCenterPaymentLink.js).
    // Only pre-fills; the operator still picks the method, reviews the message and sends.
    prefill: { type: Object, default: null },
  },

  data() {
    return {
      step: 'setup',
      amount: 0,
      selectedMethod: null,
      // 'balance' | 'deposit' — see @/utils/collectPaymentPurpose.js. Drives
      // both the OD posting intent AND whether the amount is capped at the
      // patient's balance.
      purpose: PAYMENT_PURPOSE.BALANCE,
      // Optional TreatPlanNum a deposit is attributed to. null = not tied to a plan.
      selectedTreatPlanNum: null,

      // Wiki templates
      wikiPages: [],
      wikiPagesLoaded: false,

      // Site's configured default SMS template (PAYMENT_DEFAULT_TEMPLATE_ID),
      // fetched once per mount and re-applied on each open.
      defaultSmsTemplateID: null,
      defaultSmsTemplateIDLoaded: false,

      // SMS state
      selectedSmsTemplateID: null,
      smsText: '',
      // The text we last generated from a template. smsText !== this means the
      // operator hand-edited it, so an amount change must not re-render over it.
      smsTextRendered: '',
      smsBlocked: false, // set by <SmsGuidance> when the SMS text is over the carrier limit
      lastGuidance: null, // latest <SmsGuidance> payload, for the send-time cost confirm

      // Email state
      selectedEmailTemplateID: null,

      // Terminal state
      terminalDevices: [],
      terminalDevicesLoading: false,
      terminalDevicesError: '',
      selectedTerminalId: null,
      receiptShowOnTerminal: true,
      receiptEmail: false,
      receiptEmailAddress: '',
      terminalChargeState: 'idle', // idle | sending | waiting | polling | success | partial | declined | cancelled | error
      terminalChargeMessage: '',
      terminalChargeTransactionId: null,
      terminalPollHandle: null,
      terminalPaymentID: null,
      // Amount facts from the gateway, not from the input box. On a PARTIAL
      // APPROVAL the processor approves less than we asked for (an HSA/FSA/
      // prepaid card short of the balance) and still reports success, so what
      // the operator typed is no longer what was collected.
      terminalCapturedAmount: null,
      terminalRequestedAmount: null,
      terminalShortfall: 0,
      chargingRemainder: false,
      // True when the operator clicked "Cancel charge" from the in-flight
      // UI (vs the terminal device cancelling the swipe itself). Drives
      // different copy on the cancelled-state screen because the operator
      // still needs to physically abort on the device.
      cancelledByOperator: false,
      cancellingCharge: false,
      checkingStatus: false,

      sendingInProgress: false,
    };
  },

  computed: {
    show: {
      get() { return this.value; },
      set(v) { this.$emit('input', v); },
    },

    modalTitle() {
      if (this.step === 'terminal-state') return 'Terminal Charge';
      return 'Collect Payment';
    },

    hasBalance() { return this.patientBalance > 0; },
    amountValid() { return typeof this.amount === 'number' && this.amount > 0; },

    // What the patient owes, floored at 0. This is the cap for a BALANCE
    // payment and stays the fat-finger guard on the everyday path — without it
    // a mistyped "28800" for $288.00 charges a card for $28,800.
    balanceCap() { return Math.max(this.patientBalance, 0); },

    isDepositPurpose() { return this.purpose === PAYMENT_PURPOSE.DEPOSIT; },

    // null == no cap. A deposit is a prepayment on work not yet charged, so
    // there is nothing sensible to cap it at; Vue drops a `:max` bound to null.
    maxAmount() { return this.isDepositPurpose ? null : this.balanceCap; },

    amountExceedsCap() {
      return this.maxAmount !== null
          && typeof this.amount === 'number'
          && this.amount > this.maxAmount;
    },

    // The single amount gate. Every method card, disabled reason and the
    // Continue button read this instead of re-deriving the comparison — the
    // eight copies of `!amountValid || amount > maxAmount` were how the cap
    // silently applied to a purpose that must not have one.
    amountOk() { return this.amountValid && !this.amountExceedsCap; },

    provider() {
      return this.site && this.site.PaymentProvider;
    },

    isMoolahProvider() { return this.provider === 'moolah'; },
    isStripeProvider() { return this.provider === 'stripe' && !!(this.site && this.site.StripeAccountId); },

    // Mirrors PresentDepositPanel.prepayConfigured — a prepayment needs a
    // Cat-29 UnearnedType DefNum, and without one the backend charges the card
    // and then silently fails the Open Dental write (odWrite.js throws
    // PayTypeResolutionError, writePaymentToODSafe swallows it). Never offer
    // the deposit purpose unless this resolves.
    prepayConfigured() {
      const dn = this.textToPaySettings && this.textToPaySettings.prepaymentUnearnedType;
      return Number.isFinite(parseFloat(dn)) && parseFloat(dn) > 0;
    },

    // Deposits are Moolah-only: the Stripe path (createPaymentLink) carries no
    // `intent`, so a Stripe "deposit" would post as an ordinary balance payment
    // — right money, wrong ledger.
    depositAvailable() { return this.isMoolahProvider && this.prepayConfigured; },

    depositDisabledReason() {
      if (!this.isMoolahProvider && !this.isStripeProvider) return 'No payment provider configured';
      if (!this.isMoolahProvider) return 'Treatment plan deposits require Moolah — this site uses Stripe.';
      if (!this.prepayConfigured) return 'Set Treatment Plan Prepayment in Settings → Payments.';
      return '';
    },

    purposeLabel() {
      return this.isDepositPurpose ? 'Treatment plan deposit' : 'Outstanding balance';
    },

    // Options for the optional plan-attribution dropdown. Signed plans carry no
    // financial fields, so this only sets TreatPlanNum — the operator still
    // types the amount.
    signedPlanOptions() {
      const opts = [{ value: null, text: 'Not tied to a plan' }];
      (this.signedPlans || []).forEach((p) => {
        if (!p || !p.treatPlanNum) return;
        const name = p.heading || (p.dateTP ? `Plan of ${p.dateTP}` : `Plan #${p.treatPlanNum}`);
        const signed = p.signedAt ? ` · signed ${String(p.signedAt).slice(0, 10)}` : '';
        opts.push({ value: p.treatPlanNum, text: `${name}${signed}` });
      });
      return opts;
    },

    patientPhone() { return this.patientData && this.patientData.WirelessPhone; },
    patientEmail() { return this.patientData && this.patientData.Email; },

    patientPhoneFormatted() {
      const raw = this.patientPhone || '';
      const digits = raw.replace(/\D/g, '');
      if (digits.length === 11 && digits.startsWith('1')) {
        return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
      }
      if (digits.length === 10) {
        return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
      }
      return raw || '(no phone)';
    },

    // Both lists come from @/utils/wikiTags so Settings → Payments offers
    // exactly the same set. A default the office picks there but this modal
    // can't list is a setting that can never fire.
    paymentSmsTemplates() {
      return paymentSmsTemplatesFrom(this.wikiPages);
    },

    paymentEmailTemplates() {
      // Wikis tagged BOTH "payment" and "email-template" — payment-specific
      // email templates rendered server-side via SendGrid.
      return paymentEmailTemplatesFrom(this.wikiPages);
    },

    canSms() {
      // WhatsApp-only office: the SMS / SMS + Email method cards are not offered (dev screenshot
      // pass 2026-10-07 found them selectable on this first step).
      if (this.smsOutboundOff) return false;
      if (!this.amountOk) return false;
      if (!this.patientPhone) return false;
      if (!this.isMoolahProvider && !this.isStripeProvider) return false;
      if (this.isDepositPurpose && !this.depositAvailable) return false;
      return true;
    },

    canEmail() {
      if (!this.amountOk) return false;
      if (!this.patientEmail) return false;
      if (!this.isMoolahProvider && !this.isStripeProvider) return false;
      if (this.isDepositPurpose && !this.depositAvailable) return false;
      return true;
    },

    canTerminal() {
      if (!this.amountOk) return false;
      if (!this.isMoolahProvider) return false;
      if (this.isDepositPurpose && !this.depositAvailable) return false;
      return true;
    },

    smsDisabledReason() {
      if (this.smsOutboundOff) return 'Texting is turned off for this office';
      if (!this.amountOk) return 'Set a valid amount above';
      if (!this.patientPhone) return 'No phone on file';
      if (!this.isMoolahProvider && !this.isStripeProvider) return 'No payment provider configured';
      if (this.isDepositPurpose && !this.depositAvailable) return this.depositDisabledReason;
      return '';
    },

    emailDisabledReason() {
      if (!this.amountOk) return 'Set a valid amount above';
      if (!this.patientEmail) return 'No email on file';
      if (!this.isMoolahProvider && !this.isStripeProvider) return 'No payment provider configured';
      if (this.isDepositPurpose && !this.depositAvailable) return this.depositDisabledReason;
      return '';
    },

    terminalDisabledReason() {
      if (!this.amountOk) return 'Set a valid amount above';
      if (!this.isMoolahProvider) return 'Terminal requires Moolah';
      if (this.isDepositPurpose && !this.depositAvailable) return this.depositDisabledReason;
      return '';
    },

    canContinue() {
      // The depositAvailable clause is defence-in-depth. The deposit card can't
      // be selected when it's unavailable, but if that state ever desynced we'd
      // charge a card and then silently fail the Open Dental write — the one
      // failure mode in this flow that costs a real patient real money with no
      // record. Cheap to re-assert here.
      return this.amountOk
          && (!this.isDepositPurpose || this.depositAvailable)
          && !!this.selectedMethod;
    },

    // The patient's Open Dental number, judged by the same rule the send endpoint uses.
    // Null when the number is fine. Rendered next to the Send button so a disabled button
    // is never a mystery.
    phoneProblem() {
      return describePhoneProblem(this.patientPhone);
    },

    // Shared opt-out rule (texted STOP, OD Text-OK = No, or a server/HQ block) —
    // src/utils/textConsent.js. { blocked, code, message }. Rendered next to the Send
    // button so a disabled Send-SMS is never a mystery, and folded into canSendSms.
    smsOptOutBlock() {
      return getTextBlockReason({ patient: this.patientData, timezone: this.timezone || '' });
    },

    // WhatsApp-only office (SMS_OUTBOUND_DISABLED): a payment link can't be TEXTED — and a click
    // would write the pending balance_payments row before the send is refused. Email still works.
    smsOutboundOff() {
      const settings = (this.$store.state.customer || {}).settings;
      return hasSmsNumber(settings) && isSmsOutboundDisabled(settings);
    },

    canSendSms() {
      return !this.sendingInProgress
          && !this.smsOutboundOff
          && this.selectedSmsTemplateID
          && this.smsText && this.smsText.trim().length > 0
          && !this.smsBlocked // shared SMS guidance blocks over-carrier-limit messages
          && !this.smsOptOutBlock.blocked // the patient opted out of texts — the send would be refused
          // This modal had NO phone check at all, so a payment link could be composed and
          // sent to a number main-service would reject with a 400 — after
          // createPendingPaymentRecord() had already written a balance_payments row.
          && isTextablePhone(this.patientPhone);
    },

    canSendEmail() {
      return !this.sendingInProgress && !!this.selectedEmailTemplateID;
    },

    canSendBoth() {
      return this.canSendSms && this.canSendEmail;
    },

    canSendToTerminal() {
      return !!this.selectedTerminalId
          && !this.terminalDevicesLoading
          && (!this.receiptEmail || (this.receiptEmailAddress || '').trim().length > 0);
    },

    selectedTerminalName() {
      const d = (this.terminalDevices || []).find(x => x.id === this.selectedTerminalId);
      return d ? d.name : '';
    },

    terminalChargeInFlight() {
      return this.terminalChargeState === 'sending'
          || this.terminalChargeState === 'waiting'
          || this.terminalChargeState === 'polling';
    },

    // What the card actually gave. Falls back to the requested amount when the
    // gateway reported no amount at all (older responses, some recovery paths)
    // — which is exactly the pre-2026-08-18 behaviour, and correct whenever a
    // partial approval isn't in play.
    collectedAmount() {
      return this.terminalCapturedAmount === null || this.terminalCapturedAmount === undefined
        ? this.amount
        : this.terminalCapturedAmount;
    },
  },

  watch: {
    value(newVal) {
      if (newVal) this.onOpen();
    },

    // Switching deposit → balance can strand an amount above the balance cap,
    // which would leave every method card disabled with no obvious cause. Clamp
    // it silently: the value visibly changes right under the selector the
    // operator just clicked, so a toast would only be noise. Same treatment
    // PresentDepositPanel.onAmountInput gives an over-ceiling entry.
    purpose(next) {
      if (next === PAYMENT_PURPOSE.DEPOSIT) return;
      if (typeof this.amount === 'number' && this.amount > this.balanceCap) {
        this.amount = this.balanceCap > 0 ? this.balanceCap : null;
      }
    },
  },

  mounted() {
    if (this.show) this.onOpen();
  },

  beforeDestroy() {
    this.stopTerminalPolling();
  },

  methods: {
    ...mapActions('teamio', ['sendTextMsg', 'sendEmailMsg', 'createPaymentLink']),
    ...mapActions('moolah', [
      'createMoolahPaymentRecord',
      'getMoolahDevices',
      'chargeMoolahTerminal',
      'getMoolahPaymentInfo',
      'cancelMoolahTerminalPayment',
      'getMoolahTransactionStatus',
    ]),
    ...mapActions('wiki', { getWikiPagesAction: 'getWikiPages' }),

    formatMoney(n) {
      if (n === null || n === undefined || isNaN(n)) return '$0.00';
      return `$${Number(n).toFixed(2).replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,')}`;
    },

    onOpen() {
      this.step = 'setup';
      // Balance > 0 keeps the long-standing behaviour (purpose 'balance',
      // amount pre-filled to the full balance). A $0 balance now preselects the
      // deposit purpose with an EMPTY amount field instead of a dead "0" — the
      // parent awaits the settings fetch before opening us, so depositAvailable
      // is already settled here and the card can't flip after the fact.
      const defaults = resolveCollectPaymentDefaults({
        patientBalance: this.patientBalance,
        depositAvailable: this.depositAvailable,
      });
      this.purpose = defaults.purpose;
      this.amount = defaults.amount;
      this.selectedTreatPlanNum = null;
      this.selectedMethod = null;
      this.selectedSmsTemplateID = null;
      this.smsText = '';
      this.smsTextRendered = '';
      this.selectedEmailTemplateID = null;
      this.selectedTerminalId = null;
      this.receiptShowOnTerminal = true;
      this.receiptEmail = false;
      this.receiptEmailAddress = (this.patientData && this.patientData.Email) || '';
      this.terminalDevices = [];
      this.terminalDevicesError = '';
      this.terminalChargeState = 'idle';
      this.terminalChargeMessage = '';
      this.terminalChargeTransactionId = null;
      this.terminalPaymentID = null;
      this.cancelledByOperator = false;
      this.cancellingCharge = false;
      this.checkingStatus = false;
      this.chargingRemainder = false;
      this.clearCaptureFacts();
      this.stopTerminalPolling();
      this.sendingInProgress = false;
      this.loadWikiPages();
      this.applyPrefill();
    },

    // Action Center "Send new link": start from what the failed link asked for. A deposit prefill is
    // ignored when deposits aren't available here (same gate as the deposit card), and a balance amount
    // never exceeds the balance cap the amount field enforces.
    applyPrefill() {
      const p = this.prefill;
      if (!p) return;
      const wantsDeposit = p.purpose === PAYMENT_PURPOSE.DEPOSIT && this.depositAvailable;
      this.purpose = wantsDeposit ? PAYMENT_PURPOSE.DEPOSIT : PAYMENT_PURPOSE.BALANCE;
      const planNum = Number(p.treatPlanNum);
      if (wantsDeposit && (this.signedPlans || []).some((plan) => plan && Number(plan.treatPlanNum) === planNum)) {
        this.selectedTreatPlanNum = planNum;
      }
      // After the `purpose` watcher has run — it re-clamps the amount when switching to balance.
      this.$nextTick(() => {
        const amount = Number(p.amount);
        if (!Number.isFinite(amount) || amount <= 0) return;
        if (this.purpose === PAYMENT_PURPOSE.DEPOSIT) this.amount = amount;
        else if (this.balanceCap > 0) this.amount = Math.min(amount, this.balanceCap);
      });
    },

    async loadWikiPages() {
      // The fetch is cached; re-applying the default is NOT. onOpen() nulls the
      // selection every time, so the default has to be re-applied on every open,
      // including the ones that skip the fetch.
      if (!this.wikiPagesLoaded) {
        try {
          const res = await this.getWikiPagesAction({ siteID: this.siteId });
          // store action shape: returns [pages] or { ... } depending on impl
          if (Array.isArray(res) && res[0]) {
            this.wikiPages = res[0];
          } else if (Array.isArray(res)) {
            this.wikiPages = res;
          }
          this.wikiPagesLoaded = true;
        } catch (err) {
          console.error('Failed to load wiki pages:', err);
        }
      }
      // Pages must be in hand first — the default is only honoured if it is
      // still a valid payment template, and that check needs the list.
      this.applyDefaultSmsTemplate();
    },

    /**
     * Settings → Payments has a "Default SMS Template" (PAYMENT_DEFAULT_TEMPLATE_ID).
     * Until 2026-09-16 nothing read it: the field saved, reported success, and
     * changed nothing — while `canSms` requires a template, so staff re-picked
     * one on every single send. Fetched here rather than passed down as a prop
     * so both parents (PatientProfile and TxPresenter's PresentDepositPanel)
     * get the behaviour without each wiring its own GET.
     *
     * Deliberately best-effort and non-blocking: a failed fetch, an unset
     * default, or a default whose wiki page lost its `payment` tag all degrade
     * to the old behaviour (nothing preselected). It is a convenience, never a
     * gate — the operator can always change or clear the selection.
     */
    async applyDefaultSmsTemplate() {
      // Never override a pick the operator already made this session.
      if (this.selectedSmsTemplateID) return;
      try {
        if (!this.defaultSmsTemplateIDLoaded) {
          const res = await this.$store.dispatch('teamio/getPaymentConfig', this.siteId);
          this.defaultSmsTemplateID = (res && res.config && res.config.defaultTemplateId) || null;
          this.defaultSmsTemplateIDLoaded = true;
        }
        if (!this.defaultSmsTemplateID) return;
        // Only apply a default the picker itself would offer.
        const match = this.paymentSmsTemplates.find(t => t.WikiPageID === this.defaultSmsTemplateID);
        if (!match) return;
        this.selectedSmsTemplateID = match.WikiPageID;
        this.onSmsTemplatePicked();
      } catch (err) {
        // Non-fatal by design — see the note above.
        console.error('[CollectPaymentModal] could not resolve default SMS template:', err);
      }
    },

    /**
     * Wiki templates are stored as EditorJS JSON in `Content`. Render them
     * to plaintext SMS the same way CreateFollowup does for the Aging
     * "Send Payment SMS" path:
     *   1. Parse JSON, pull paragraph blocks
     *   2. Convert links (forms/packets/external) to readable [Form: …]
     *      placeholders so they don't appear as raw HTML in the textarea
     *   3. Strip remaining HTML to plaintext via a temp DIV
     *   4. Substitute available {{variables}} the operator can pre-fill
     *      (patient_first_name, etc). {{payment_link}} stays intact —
     *      backend resolver fills it at send time, see PAYMENT_SYSTEM.md
     *      § 4 "DO NOT pre-substitute on the frontend for Moolah".
     */
    onSmsTemplatePicked() {
      const t = this.paymentSmsTemplates.find(x => x.WikiPageID === this.selectedSmsTemplateID);
      if (!t) { this.smsText = ''; this.smsTextRendered = ''; return; }
      this.smsText = this.renderWikiTemplateToSmsText(t.Content || '');
      this.smsTextRendered = this.smsText;
    },

    /**
     * Re-render the SMS from the CURRENT amount as the operator enters an SMS step.
     *
     * Why: {{patient_balance}} is substituted at render time from `this.amount`.
     * Since applyDefaultSmsTemplate() pre-selects the site default when the modal
     * OPENS, that first render happens before the operator has set the amount —
     * so changing $3,061.60 to a $500 partial left the text saying $3,061.60 while
     * the link charged $500. (Found 2026-09-16 before it deployed; before the
     * default existed, templates were only picked on this step, after the amount.)
     *
     * Hand-edited text is the operator's and is never overwritten: we only
     * re-render when the text is still exactly what we last generated.
     */
    refreshSmsTextForCurrentAmount() {
      if (!this.selectedSmsTemplateID) {
        // Default not applied yet (slow config fetch, or none configured).
        this.applyDefaultSmsTemplate();
        return;
      }
      if (this.smsText !== this.smsTextRendered) return;
      this.onSmsTemplatePicked();
    },

    // Shared SMS guidance (<SmsGuidance>): block Send when the SMS text is over the
    // carrier limit. canSendSms/canSendBoth already fold in smsBlocked. Both the SMS-only
    // and both-configure steps render <SmsGuidance> over the same smsText into this one
    // handler (only one step is mounted at a time), so a single lastGuidance covers both.
    onSmsGuidance(g) {
      this.smsBlocked = !!(g && g.blocked);
      this.lastGuidance = g || null;
    },

    renderWikiTemplateToSmsText(rawContent) {
      let parsed;
      try { parsed = JSON.parse(rawContent); }
      catch (_) {
        // Not EditorJS JSON — assume it's already plaintext (legacy
        // templates) and return as-is.
        return rawContent || '';
      }
      if (!parsed || !Array.isArray(parsed.blocks)) return '';

      const convertLinksToPlaceholders = (html) => {
        return (html || '').replace(/<a\s+href="([^"]*)"[^>]*>(.*?)<\/a>/gi, (_match, href, displayText) => {
          if (!displayText || displayText.trim() === '') return '';
          const cleanText = displayText.replace(/<[^>]*>/g, '').trim();
          if (href.indexOf('patient_link_for_form') >= 0) return `[Form: ${cleanText}]`;
          if (href.indexOf('patient_link_for_packet') >= 0) return `[Packet: ${cleanText}]`;
          return `[Link: ${cleanText}]`;
        });
      };

      let smsText = '';
      const paragraphs = parsed.blocks.filter(b => b.type === 'paragraph');
      for (const b of paragraphs) {
        const withPlaceholders = convertLinksToPlaceholders(b.data && b.data.text);
        const tmp = (typeof document !== 'undefined') ? document.createElement('DIV') : null;
        if (tmp) {
          tmp.innerHTML = withPlaceholders;
          smsText += (tmp.textContent || tmp.innerText || '');
        } else {
          // Fallback — strip tags via regex when no DOM is available
          smsText += withPlaceholders.replace(/<[^>]*>/g, '');
        }
      }
      smsText = smsText.replace(/\s+$/, '');

      const site = (this.site || {});
      const vars = {
        patient_first_name: this.patientData && this.patientData.FName,
        patient_last_name:  this.patientData && this.patientData.LName,
        patient_name:       this.patientData && this.patientData.PatientName,
        patient_wireless_phone: this.patientPhone,
        site_id:        this.siteId,
        site_name:      (this.user && this.user.SiteName) || site.Name || '',
        site_address1:  site.Address1 || '',
        site_address2:  site.Address2 || '',
        site_city:      site.City || '',
        site_state:     site.State || '',
        site_zip:       site.Zip || '',
        site_officephone: site.OfficePhone || '',
        employee_name:  (this.user && this.user.Name) || '',
      };
      if (typeof this.amount === 'number' && this.amount > 0) {
        vars.patient_balance = this.amount.toFixed(2).replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,');
      }

      // Replace {{var}} where var is in vars. {{payment_link}} is left
      // intact — see method docstring.
      let out = smsText;
      const re = /\{\{([^}]+?)\}\}/g;
      let m;
      while ((m = re.exec(out)) !== null) {
        const key = m[1].trim();
        if (Object.prototype.hasOwnProperty.call(vars, key) && vars[key] !== undefined && vars[key] !== null) {
          out = out.replace(m[0], vars[key]);
          re.lastIndex = 0; // re-scan from the start since the string changed
        }
      }
      return out;
    },

    onContinue() {
      if (!this.canContinue) return;
      if (this.selectedMethod === 'sms') {
        this.step = 'sms-configure';
        this.refreshSmsTextForCurrentAmount();
      } else if (this.selectedMethod === 'email') {
        this.step = 'email-configure';
      } else if (this.selectedMethod === 'both') {
        this.step = 'both-configure';
        this.refreshSmsTextForCurrentAmount();
      } else if (this.selectedMethod === 'terminal') {
        this.step = 'terminal-configure';
        this.fetchTerminalDevices();
      }
    },

    goBackToSetup() {
      if (this.terminalChargeInFlight) {
        this.$toast.warning('A charge is in progress — wait for it to finish.');
        return;
      }
      this.step = 'setup';
    },

    closeModal() {
      if (this.terminalChargeInFlight) {
        const ok = window.confirm(
          `Payment is in progress on ${this.selectedTerminalName || 'the terminal'}. ` +
          `Closing won't abort the charge — verify in Moolah reports if it goes through. Close anyway?`
        );
        if (!ok) return;
      }
      this.show = false;
    },

    onHidden() {
      this.stopTerminalPolling();
    },

    // ─── Common ─────────────────────────────────────────────────────────

    // Extra payload fields that turn a payment record into a treatment-plan
    // prepayment. Empty object for a balance payment, so that request shape is
    // unchanged from before this feature.
    depositIntentFields() {
      return buildDepositIntentFields({
        purpose: this.purpose,
        treatPlanNum: this.selectedTreatPlanNum,
      });
    },

    async createPendingPaymentRecord() {
      // Both Moolah and Stripe paths need a balance_payments row created
      // first. For Moolah the row's Balance is what the patient pays via
      // the hosted page or terminal; for Stripe the row mirrors the link.
      //
      // Deposits never reach the Stripe branch: createPaymentLink carries no
      // `intent`, so a Stripe deposit would post to Open Dental as an ordinary
      // balance payment. The UI already gates this; the throw is the backstop.
      if (this.isDepositPurpose && !this.isMoolahProvider) {
        throw new Error('Treatment plan deposits require Moolah');
      }
      if (this.isMoolahProvider) {
        const created = await this.createMoolahPaymentRecord({
          siteID: this.siteId,
          patNum: this.patNum,
          balance: this.amount,
          patientName: this.patientData.PatientName ||
                       `${this.patientData.FName || ''} ${this.patientData.LName || ''}`.trim(),
          patientPhone: this.patientPhone,
          // This record is about to be delivered to the patient as a link, so
          // the backend logs an Open Dental Account-module statement for it
          // (balance sends only). Set here rather than in the three send
          // methods so SMS, Email and SMS+Email share one record and therefore
          // produce exactly one statement. sendToTerminal() builds its own
          // record and deliberately omits this.
          sendsLink: true,
          ...this.depositIntentFields(),
        });
        return { paymentID: created.paymentID, paymentUrl: null, totalCharge: this.amount, processingFee: 0 };
      }
      if (this.isStripeProvider) {
        const resp = await this.createPaymentLink({
          siteID: this.siteId,
          patNum: this.patNum,
          balance: this.amount,
          patientName: this.patientData.PatientName ||
                       `${this.patientData.FName || ''} ${this.patientData.LName || ''}`.trim(),
          patientPhone: this.patientPhone,
        });
        if (!resp || !resp.paymentUrl) throw new Error('Failed to create Stripe payment link');
        return {
          paymentID: resp.paymentID || null,
          paymentUrl: resp.paymentUrl,
          totalCharge: resp.totalCharge,
          originalBalance: resp.originalBalance,
          stripeFee: resp.stripeFee,
        };
      }
      throw new Error('No payment provider configured for this site');
    },

    substituteStripeVars(text, info) {
      let s = text || '';
      if (info && info.paymentUrl) {
        s = s.replace(/\{\{payment_link\}\}/gi, info.paymentUrl);
        s = s.replace(/\{payment_link\}/gi, info.paymentUrl);
      }
      if (info && info.totalCharge !== undefined) {
        s = s.replace(/\{\{total_amount\}\}/gi, this.formatMoney(info.totalCharge));
      }
      if (info && info.originalBalance !== undefined) {
        s = s.replace(/\{\{balance\}\}/gi, this.formatMoney(info.originalBalance));
      }
      return s;
    },

    // A /send-text-msg 200 body of { success:false, ... } is a REFUSAL (opt-out /
    // undeliverable), NOT a send — a successful send returns the bare string "success".
    // Returns { refused, reason }; the reason is the opt-out reason, else the body message.
    sendRefusal(resp) {
      const data = resp && resp.data;
      if (data && data.success === false) {
        const optOut = optOutResultFromSendFailure(data);
        return { refused: true, reason: (optOut && optOut.reason) || data.message || 'The text message was not sent.' };
      }
      return { refused: false, reason: '' };
    },

    // ─── SMS ─────────────────────────────────────────────────────────────
    async sendSmsOnly() {
      if (!this.canSendSms) return;

      this.sendingInProgress = true;
      try {
        const info = await this.createPendingPaymentRecord();
        const textToSend = this.isStripeProvider
          ? this.substituteStripeVars(this.smsText, info)
          : this.smsText; // Moolah: backend resolves {{payment_link}}
        const resp = await this.sendTextMsg({
          siteID: this.siteId,
          patNum: this.patNum,
          smsText: textToSend,
          defaultDB: this.user && this.user.DefaultDB,
          userODNum: this.user && this.user.ODUserNum,
          // Marks this as a staff send ('manual') in sms_send_log + alerts, not 'unknown'.
          employeeName: (this.user && this.user.Name) || undefined,
        });
        // A 200 refusal (opt-out / undeliverable) must not read as sent — surface the reason
        // through the existing catch instead of the success toast + $emit('sent').
        const refusal = this.sendRefusal(resp);
        if (refusal.refused) throw new Error(refusal.reason);
        this.$toast.success(`Payment SMS sent for ${this.formatMoney(this.amount)}.`);
        this.$emit('sent', { method: 'sms', amount: this.amount });
        this.show = false;
      } catch (err) {
        console.error('sendSmsOnly failed:', err);
        this.$toast.error(err.response?.data?.message || err.message || 'Failed to send payment SMS');
      } finally {
        this.sendingInProgress = false;
      }
    },

    // ─── EMAIL ───────────────────────────────────────────────────────────
    async sendEmailOnly() {
      if (!this.canSendEmail) return;
      this.sendingInProgress = true;
      try {
        await this.createPendingPaymentRecord();
        await this.sendEmailMsg({
          siteID: this.siteId,
          patNum: this.patNum,
          emailTemplateID: this.selectedEmailTemplateID,
        });
        this.$toast.success(`Payment email sent for ${this.formatMoney(this.amount)}.`);
        this.$emit('sent', { method: 'email', amount: this.amount });
        this.show = false;
      } catch (err) {
        console.error('sendEmailOnly failed:', err);
        this.$toast.error(err.response?.data?.message || err.message || 'Failed to send payment email');
      } finally {
        this.sendingInProgress = false;
      }
    },

    // ─── BOTH ────────────────────────────────────────────────────────────
    async sendBoth() {
      if (!this.canSendBoth) return;

      this.sendingInProgress = true;
      try {
        // Single balance_payments row covers both channels — they share the
        // same payment_link.
        const info = await this.createPendingPaymentRecord();
        const textToSend = this.isStripeProvider
          ? this.substituteStripeVars(this.smsText, info)
          : this.smsText;

        // Send in parallel; if one fails the other still gets out.
        const [smsRes, emailRes] = await Promise.allSettled([
          this.sendTextMsg({
            siteID: this.siteId,
            patNum: this.patNum,
            smsText: textToSend,
            defaultDB: this.user && this.user.DefaultDB,
            userODNum: this.user && this.user.ODUserNum,
            employeeName: (this.user && this.user.Name) || undefined,
          }),
          this.sendEmailMsg({
            siteID: this.siteId,
            patNum: this.patNum,
            emailTemplateID: this.selectedEmailTemplateID,
          }),
        ]);

        // A resolved SMS promise can still be a 200 refusal (opt-out / undeliverable) — that
        // is NOT "sent". Fold the refusal into smsOk so the mixed-result copy is honest.
        const smsRefusal = smsRes.status === 'fulfilled' ? this.sendRefusal(smsRes.value) : { refused: false, reason: '' };
        const smsOk = smsRes.status === 'fulfilled' && !smsRefusal.refused;
        const emailOk = emailRes.status === 'fulfilled';
        if (smsOk && emailOk) {
          this.$toast.success(`Payment SMS + Email sent for ${this.formatMoney(this.amount)}.`);
          this.$emit('sent', { method: 'both', amount: this.amount });
          this.show = false;
        } else if (smsOk) {
          this.$toast.warning('SMS sent, but email failed. Check the email template + patient email.');
        } else if (emailOk) {
          this.$toast.warning(smsRefusal.refused
            ? `Email sent, but the text was not: ${smsRefusal.reason}`
            : 'Email sent, but SMS failed. Check the patient phone number.');
        } else {
          this.$toast.error('Both SMS and email failed. Please try again.');
        }
      } catch (err) {
        console.error('sendBoth failed:', err);
        this.$toast.error(err.response?.data?.message || err.message || 'Failed to send payment');
      } finally {
        this.sendingInProgress = false;
      }
    },

    // ─── TERMINAL ────────────────────────────────────────────────────────
    // Filter out virtual / test-only terminals at the UI layer — operators
    // shouldn't be picking those for real patient charges. The backend
    // still returns them with isVirtual: true so other tooling (e.g. an
    // admin diagnostic page) can opt in if needed; we just don't surface
    // them here.
    async fetchTerminalDevices() {
      this.terminalDevicesLoading = true;
      this.terminalDevicesError = '';
      try {
        const resp = await this.getMoolahDevices(this.siteId);
        const all = (resp && resp.devices) || [];
        this.terminalDevices = all.filter(d => !d.isVirtual);
        if (this.terminalDevices.length === 1) {
          this.selectedTerminalId = this.terminalDevices[0].id;
        }
      } catch (err) {
        console.error('fetchTerminalDevices failed:', err);
        this.terminalDevicesError = extractApiError(err, 'Failed to load terminals');
        this.terminalDevices = [];
      } finally {
        this.terminalDevicesLoading = false;
      }
    },

    async sendToTerminal() {
      if (!this.canSendToTerminal) return;
      this.step = 'terminal-state';
      this.terminalChargeState = 'sending';
      this.terminalChargeMessage = '';
      this.terminalChargeTransactionId = null;
      // Stale amounts from a previous attempt would make the next result read as
      // a partial approval that never happened.
      this.clearCaptureFacts();

      try {
        // NOTE: the terminal path builds its own record rather than going
        // through createPendingPaymentRecord — the intent fields have to be
        // threaded in BOTH places.
        const created = await this.createMoolahPaymentRecord({
          siteID: this.siteId,
          patNum: this.patNum,
          balance: this.amount,
          patientName: this.patientData.PatientName ||
                       `${this.patientData.FName || ''} ${this.patientData.LName || ''}`.trim(),
          patientPhone: this.patientPhone,
          ...this.depositIntentFields(),
        });
        this.terminalPaymentID = created.paymentID;

        this.terminalChargeState = 'waiting';
        let resp;
        try {
          resp = await this.chargeMoolahTerminal({
            siteID: this.siteId,
            paymentID: this.terminalPaymentID,
            deviceId: this.selectedTerminalId,
            receiptOptions: {
              showOnTerminal: this.receiptShowOnTerminal,
              emailReceipt: this.receiptEmail,
              emailAddress: this.receiptEmail ? (this.receiptEmailAddress || '').trim() : '',
            },
          });
        } catch (err) {
          // Shared with PresentDepositPanel — see @/utils/terminalChargeError.js.
          const outcome = classifyTerminalChargeError(err);
          if (outcome === TERMINAL_CHARGE_OUTCOME.PENDING) {
            // Expected: the Lambda waits ~170s, axios gives up at 8s.
            this.terminalChargeState = 'polling';
            this.terminalChargeMessage = 'Charge in progress on terminal — checking status…';
            this.startTerminalPolling();
            return;
          }
          // A 409 is a deliberate refusal, not a fault — never render it as one.
          this.terminalChargeState = outcome === TERMINAL_CHARGE_OUTCOME.BLOCKED ? 'blocked' : 'error';
          this.terminalChargeMessage = terminalChargeErrorMessage(err);
          return;
        }

        this.applyTerminalChargeResult(resp);
      } catch (err) {
        console.error('sendToTerminal setup failed:', err);
        this.terminalChargeState = 'error';
        this.terminalChargeMessage = extractApiError(err, 'Failed to start the charge');
      }
    },

    applyTerminalChargeResult(resp) {
      this.terminalChargeMessage = (resp && resp.message) || '';
      // Preserve any earlier-stored transaction ID if the recovery path
      // didn't surface one — paid → success path always has it; some
      // poll branches won't.
      if (resp && resp.transactionId) {
        this.terminalChargeTransactionId = resp.transactionId;
      }
      // Amount facts, when the responding path knew them. Only overwrite on a
      // response that actually carries a captured amount — a later recovery
      // response with no amount block must not erase what the charge reported.
      if (resp && resp.capturedAmount !== undefined && resp.capturedAmount !== null) {
        this.terminalCapturedAmount = resp.capturedAmount;
        this.terminalRequestedAmount = resp.requestedAmount !== undefined && resp.requestedAmount !== null
          ? resp.requestedAmount
          : this.amount;
        this.terminalShortfall = resp.shortfall || 0;
      }
      if (resp && resp.partialApproval === true) {
        // Still a real payment — the money moved, it was just less than asked.
        // Distinct state so the shortfall can't be mistaken for a clean capture.
        this.terminalChargeState = 'partial';
        this.$emit('sent', { method: 'terminal', amount: this.collectedAmount, partialApproval: true });
      } else if (resp && (resp.status === 'paid' || resp.success === true)) {
        this.terminalChargeState = 'success';
        this.$emit('sent', { method: 'terminal', amount: this.collectedAmount });
      } else if (resp && resp.status === 'cancelled') {
        this.terminalChargeState = 'cancelled';
      } else if (resp && resp.status === 'unresolved') {
        // Terminal charge timed out — outcome UNKNOWN. A card swiped after our
        // wait can still charge out-of-band (backend PAYMENT_SYSTEM.md §11.25/26),
        // so this is NOT a decline/failure: warn, do not invite a blind re-charge.
        this.terminalChargeState = 'unconfirmed';
      } else if (resp && (resp.status === 'failed' || resp.status === 'voided' || resp.status === 'expired')) {
        this.terminalChargeState = 'declined';
      } else {
        this.terminalChargeState = 'error';
        this.terminalChargeMessage = this.terminalChargeMessage || `Unexpected status: ${resp && resp.status}`;
      }
    },

    // 240-second polling window (matches the deleted CreateFollowup version).
    //
    // Two layers:
    //   1. Every tick: cheap DB-only lookup via getMoolahPaymentInfo. Fast
    //      path when our chargeMoolahTerminal Lambda finished its write.
    //   2. Every Nth tick (RECOVERY_TICK_INTERVAL): if our DB still says
    //      pending/processing and we have a MoolahTransactionId, query
    //      Moolah's GET /api/transactions/{uuid} as a backup signal so a
    //      stuck-pending row can recover even if the original Lambda's
    //      DB write got lost. The endpoint reconciles + returns the live
    //      status — see backend index.js getMoolahTransactionStatus.
    startTerminalPolling() {
      const startedAt = Date.now();
      const POLL_INTERVAL_MS = 1000;
      const POLL_TIMEOUT_MS = 240000;
      const RECOVERY_TICK_INTERVAL = 15; // ~ every 15s, well below Moolah's 90s typical swipe window
      let tickCount = 0;

      const tick = async () => {
        tickCount += 1;
        if (Date.now() - startedAt > POLL_TIMEOUT_MS) {
          this.stopTerminalPolling();
          // Unknown outcome, not an error: a card swiped after the wait can still
          // charge out-of-band (backend §11.25/26). Frame as "unconfirmed" and
          // warn against a blind re-charge.
          this.terminalChargeState = 'unconfirmed';
          this.terminalChargeMessage = 'We could not confirm this charge in time. If a card was charged it will post automatically. Do NOT re-charge; verify in Moolah reports first.';
          return;
        }
        try {
          const info = await this.getMoolahPaymentInfo(this.terminalPaymentID);
          const status = info && info.status;
          if (status === 'paid') {
            this.stopTerminalPolling();
            // The 8s axios timeout means this poll — NOT chargeMoolahTerminal's
            // own response — is how the modal usually learns the outcome, so the
            // partial-approval facts have to travel on this path too.
            this.applyTerminalChargeResult({
              success: true,
              status: 'paid',
              transactionId: info.moolahTransactionId,
              capturedAmount: info.capturedAmount,
              requestedAmount: info.requestedAmount,
              partialApproval: info.partialApproval === true,
              shortfall: info.shortfall,
            });
            return;
          }
          if (status === 'failed' || status === 'cancelled' || status === 'voided' || status === 'expired') {
            this.stopTerminalPolling();
            this.applyTerminalChargeResult({ success: false, status });
            return;
          }
          // Moolah gives up after ~90s without an answer (504 TERMINAL_NO_RESPONSE) and the
          // backend marks the row 'unresolved' — outcome UNKNOWN (payment-service §11.32).
          // Say so now instead of spinning to the 240s window, but KEEP polling: a late swipe
          // still flips the row to 'paid', and that must still turn this into "Payment captured".
          if (status === 'unresolved' && this.terminalChargeState !== 'unconfirmed') {
            this.applyTerminalChargeResult({
              success: false,
              status: 'unresolved',
              message: 'The terminal did not confirm this charge in time.',
            });
          }
          // Still pending/processing/sent — every ~15s consult Moolah
          // directly. Best-effort: failure is silent; the next DB-only
          // tick will keep watching anyway. An 'unresolved' row has NO Moolah
          // transaction id (the lost-reply case, payment-service §11.33), so it must
          // still be asked: the backend then asks Moolah's reconcile for the answer.
          if (tickCount % RECOVERY_TICK_INTERVAL === 0 && info && (info.moolahTransactionId || status === 'unresolved')) {
            try {
              const live = await this.getMoolahTransactionStatus({
                siteID: this.siteId,
                paymentID: this.terminalPaymentID,
              });
              if (live && live.dbUpdated) {
                console.log(`Polling recovery: reconciled to ${live.liveStatus} via /api/transactions`);
              }
              if (live && (live.liveStatus === 'paid' || live.liveStatus === 'failed' ||
                           live.liveStatus === 'cancelled' || live.liveStatus === 'voided')) {
                this.stopTerminalPolling();
                this.applyTerminalChargeResult({
                  success: live.success,
                  status: live.liveStatus,
                  message: live.message,
                  transactionId: live.transactionId,
                  capturedAmount: live.capturedAmount,
                  requestedAmount: live.requestedAmount,
                  partialApproval: live.partialApproval === true,
                  shortfall: live.shortfall,
                });
              }
            } catch (recErr) {
              console.warn('recovery query failed:', recErr.message);
            }
          }
        } catch (err) {
          console.warn('terminal status poll failed:', err.message);
        }
      };

      tick();
      this.terminalPollHandle = setInterval(tick, POLL_INTERVAL_MS);
    },

    /**
     * Operator clicked "Check status" — manual recovery query against
     * Moolah's GET /api/transactions/{uuid}. Same backend endpoint the
     * polling uses; useful when the operator can SEE the terminal said
     * "completed" but our spinner is still going. Stops polling on a
     * terminal status, leaves it running otherwise.
     */
    async manualCheckStatus() {
      if (this.checkingStatus) return;
      if (!this.terminalPaymentID) return;
      this.checkingStatus = true;
      try {
        const live = await this.getMoolahTransactionStatus({
          siteID: this.siteId,
          paymentID: this.terminalPaymentID,
        });
        if (live && (live.liveStatus === 'paid' || live.liveStatus === 'failed' ||
                     live.liveStatus === 'cancelled' || live.liveStatus === 'voided')) {
          this.stopTerminalPolling();
          this.applyTerminalChargeResult({
            success: live.success,
            status: live.liveStatus,
            message: live.message,
            transactionId: live.transactionId,
            capturedAmount: live.capturedAmount,
            requestedAmount: live.requestedAmount,
            partialApproval: live.partialApproval === true,
            shortfall: live.shortfall,
          });
        } else if (live && live.reason === 'no_moolah_transaction_id_yet') {
          this.$toast.info('Moolah hasn\'t reported back yet — keep waiting or hit Cancel.');
        } else {
          this.$toast.info('No update from Moolah yet.');
        }
      } catch (err) {
        console.error('manualCheckStatus failed:', err);
        this.$toast.error(extractApiError(err, 'Failed to fetch live status'));
      } finally {
        this.checkingStatus = false;
      }
    },

    stopTerminalPolling() {
      if (this.terminalPollHandle) {
        clearInterval(this.terminalPollHandle);
        this.terminalPollHandle = null;
      }
    },

    resetTerminalToConfigure() {
      this.stopTerminalPolling();
      this.terminalChargeState = 'idle';
      this.terminalChargeMessage = '';
      this.terminalChargeTransactionId = null;
      this.terminalPaymentID = null;
      this.cancelledByOperator = false;
      this.clearCaptureFacts();
      this.step = 'terminal-configure';
    },

    clearCaptureFacts() {
      this.terminalCapturedAmount = null;
      this.terminalRequestedAmount = null;
      this.terminalShortfall = 0;
    },

    /**
     * Partial approval → collect the rest on another card, right now, while the
     * patient is still at the desk.
     *
     * This starts a genuinely NEW payment for the shortfall — it is not a retry.
     * Safe against the backend's duplicate-charge guards: the first row is
     * terminal ('paid'), so neither the cross-row in-flight mutex nor
     * createMoolahPaymentRecord's pending-row reuse can match it. Re-running the
     * ORIGINAL amount is what would double-charge, which is why this button
     * charges the remainder and never the full amount again.
     */
    async chargeRemainder() {
      if (this.chargingRemainder) return;
      const remaining = Number(this.terminalShortfall);
      if (!Number.isFinite(remaining) || remaining <= 0) return;

      this.chargingRemainder = true;
      try {
        this.stopTerminalPolling();
        this.amount = remaining;
        this.terminalPaymentID = null;
        this.terminalChargeTransactionId = null;
        this.terminalChargeMessage = '';
        this.cancelledByOperator = false;
        this.clearCaptureFacts();
        await this.sendToTerminal();
      } finally {
        this.chargingRemainder = false;
      }
    },

    /**
     * Operator clicked "Cancel charge" from the in-flight UI. We can't
     * actually abort the swipe on Moolah's side — that requires hitting
     * Cancel/Back on the physical terminal — but we can:
     *   1) stop our local polling so the spinner lets go
     *   2) flip our balance_payments row to 'cancelled' so it doesn't
     *      sit in 'pending' forever (and so a late Lambda return is
     *      a no-op against a terminal status)
     *   3) move to the cancelled-state UI with copy that nudges the
     *      operator to also hit Cancel on the device
     */
    // `unconfirmed === true` only from the "Charge not confirmed" screen. Strict check on
    // purpose: the in-flight button binds this method bare, so Vue passes the click event.
    async cancelInFlightCharge(unconfirmed) {
      if (this.cancellingCharge) return;
      const ok = window.confirm(unconfirmed === true
        ? `Cancel this unconfirmed charge?\n\n` +
          `Only do this if you've confirmed the card was NOT charged. ` +
          `Also press Cancel/Back on the terminal — otherwise the patient could still complete it.`
        : `Stop waiting for this terminal charge?\n\n` +
          `This won't automatically abort the swipe on the terminal — ` +
          `you'll still need to press Cancel/Back on the device.`
      );
      if (!ok) return;

      this.cancellingCharge = true;
      this.stopTerminalPolling();
      try {
        if (this.terminalPaymentID) {
          await this.cancelMoolahTerminalPayment({
            siteID: this.siteId,
            paymentID: this.terminalPaymentID,
          });
        }
      } catch (err) {
        // Best-effort. Even if the DB flip fails (network, race with
        // a paid-row already), we still want to leave the UI cleanly.
        console.warn('cancelInFlightCharge: backend cancel failed:', err && err.message);
      } finally {
        this.cancellingCharge = false;
        this.cancelledByOperator = true;
        this.terminalChargeState = 'cancelled';
        this.terminalChargeMessage = '';
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.cp-setup, .cp-configure, .cp-state-block {
  padding: 0.5rem 0.25rem 0;
}

.cp-balance-row {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  padding: 0.75rem 1rem;
  background: #f8f9fa;
  border-radius: 6px;
  margin-bottom: 1.25rem;
}
.cp-balance-label { color: #6c757d; font-size: 0.9rem; }
.cp-balance-value {
  font-size: 1.5rem;
  font-weight: 600;
  color: #28a745;
  &.cp-balance-zero { color: #6c757d; }
}

.cp-amount-block { margin-bottom: 0.75rem; }
.cp-label {
  display: block;
  font-weight: 600;
  font-size: 0.9rem;
  color: #495057;
  margin-bottom: 0.4rem;
}
.cp-amount-input-group {
  max-width: 260px;

  ::v-deep .input-group-text {
    background: #f1f3f5;
    border-color: #ced4da;
    font-weight: 600;
    color: #495057;
  }
  ::v-deep .form-control {
    font-size: 1.1rem;
    font-weight: 500;
  }
}

.cp-help-text {
  font-size: 0.8rem;
  color: #6c757d;
  margin-top: 0.4rem;
  margin-bottom: 0;
  code { background: #f1f3f5; padding: 0.1rem 0.3rem; border-radius: 3px; }
}

.cp-section-title {
  margin-top: 0.5rem;
  margin-bottom: 0.75rem;
  font-weight: 600;
  color: #495057;
}

.cp-purpose-block {
  margin-bottom: 1.25rem;
}

.cp-purpose-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.75rem;
}

/* Same visual language as .cp-method-card, tighter — this is a mode switch,
   not one of the four collection methods, so it must read as secondary. */
.cp-purpose-card {
  background: white;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  padding: 0.7rem 0.9rem;
  text-align: left;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover:not(.disabled) {
    border-color: #b0b0b0;
    background: #fafafa;
  }
  &.active {
    border-color: #28a745;
    background: #f0fff4;
  }
  &.disabled {
    cursor: not-allowed;
    opacity: 0.6;
    background: #f8f9fa;
  }

  .cp-purpose-title { font-weight: 600; font-size: 0.95rem; color: #333; }
  .cp-purpose-sub { font-size: 0.78rem; color: #6c757d; margin-top: 0.15rem; }
  .cp-purpose-disabled-reason {
    font-size: 0.75rem;
    color: #dc3545;
    margin-top: 0.35rem;
    font-style: italic;
  }
}

.cp-plan-select {
  margin-top: 0.9rem;
  max-width: 360px;
}

/* Carries the purpose onto every confirm/success screen. With the balance cap
   lifted on deposits this label is the operator's last chance to notice a
   mistyped amount before the card is charged. */
.cp-purpose-badge {
  display: inline-block;
  margin-left: 0.5rem;
  padding: 0.1rem 0.45rem;
  border-radius: 10px;
  background: #d3f9d8;
  color: #2b8a3e;
  font-size: 0.72rem;
  font-weight: 600;
  vertical-align: middle;
}

.cp-method-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.75rem;
  margin-bottom: 1.25rem;
}

.cp-method-card {
  background: white;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  padding: 1rem;
  text-align: left;
  cursor: pointer;
  transition: all 0.15s ease;
  position: relative;

  &:hover:not(.disabled) {
    border-color: #b0b0b0;
    background: #fafafa;
  }
  &.active {
    border-color: #28a745;
    background: #f0fff4;
    .cp-method-icon { color: #28a745; }
  }
  &.disabled {
    cursor: not-allowed;
    opacity: 0.6;
    background: #f8f9fa;
  }

  .cp-method-icon {
    font-size: 1.5rem;
    color: #547fff;
    margin-bottom: 0.4rem;
  }
  .cp-method-title { font-weight: 600; font-size: 1rem; color: #333; }
  .cp-method-sub { font-size: 0.8rem; color: #6c757d; margin-top: 0.2rem; }
  .cp-method-disabled-reason {
    font-size: 0.75rem;
    color: #dc3545;
    margin-top: 0.4rem;
    font-style: italic;
  }
}

.cp-summary-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #e7f5ff;
  padding: 0.6rem 0.9rem;
  border-radius: 6px;
  margin-bottom: 1rem;
  font-size: 0.95rem;
  strong { color: #1864ab; }
}

.cp-section {
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  margin-bottom: 1rem;
  overflow: hidden;
}
.cp-section-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.65rem 1rem;
  background: #f8f9fa;
  border-bottom: 1px solid #e0e0e0;
}
.cp-section-icon { color: #547fff; font-size: 1rem; width: 18px; text-align: center; }
.cp-section-title-text {
  font-weight: 600;
  font-size: 0.95rem;
  color: #333;
  flex: 1;
}
.cp-section-action { padding: 0; font-size: 0.85rem; }
.cp-section-body { padding: 0.85rem 1rem; }

.cp-empty-state {
  text-align: center;
  padding: 1.25rem 0.75rem;
  background: #f8f9fa;
  border-radius: 6px;

  &.cp-empty-error {
    background: #fff5f5;
    border: 1px solid #ffe3e3;
    .cp-empty-icon { color: #fa5252; }
  }
}
.cp-empty-icon {
  font-size: 2rem;
  color: #adb5bd;
  margin-bottom: 0.5rem;
  display: block;
}
.cp-empty-title {
  font-weight: 600;
  font-size: 1rem;
  color: #495057;
  margin-bottom: 0.25rem;
}
.cp-empty-sub {
  font-size: 0.85rem;
  color: #6c757d;
  max-width: 380px;
  margin: 0 auto 0.75rem;
  line-height: 1.4;
}
.cp-empty-actions {
  display: flex;
  gap: 0.5rem;
  justify-content: center;
  flex-wrap: wrap;
}

.cp-terminal-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
.cp-terminal-card {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  background: white;
  border: 2px solid #e0e0e0;
  border-radius: 6px;
  padding: 0.75rem 1rem;
  cursor: pointer;
  text-align: left;
  transition: all 0.15s ease;

  &:hover { border-color: #b0b0b0; background: #fafafa; }
  &.active {
    border-color: #28a745;
    background: #f0fff4;
    .cp-terminal-radio i { color: #28a745; }
  }
}
.cp-terminal-radio i { font-size: 1.1rem; color: #aaa; }
.cp-terminal-info { flex: 1; min-width: 0; }
.cp-terminal-name {
  font-weight: 600;
  font-size: 0.95rem;
  color: #333;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
}
.cp-terminal-serial { font-size: 0.75rem; color: #6c757d; margin-top: 0.1rem; }
.cp-terminal-badge {
  display: inline-flex;
  align-items: center;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  padding: 0.2rem 0.55rem;
  background: #fff3cd;
  color: #856404;
  border: 1px solid #ffeeba;
  border-radius: 10px;
}
.cp-terminal-card.virtual {
  background: #fffaf0;
  &.active { background: #fff8e1; border-color: #ff9800; }
}

.cp-toggle-row {
  display: flex;
  align-items: flex-start;
  gap: 0.65rem;
  padding: 0.6rem 0.75rem;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  cursor: pointer;
  margin-bottom: 0.5rem;
  transition: all 0.15s ease;
  margin-left: 0;

  &:hover { background: #fafafa; }
  &.active {
    border-color: #28a745;
    background: #f0fff4;
    .cp-toggle-icon i { color: #28a745; }
  }
}
.cp-toggle-input { display: none; }
.cp-toggle-icon { padding-top: 1px; }
.cp-toggle-icon i { font-size: 1.1rem; color: #aaa; }
.cp-toggle-content { display: flex; flex-direction: column; flex: 1; min-width: 0; }
.cp-toggle-title { font-weight: 500; font-size: 0.9rem; color: #333; }
.cp-toggle-sub { font-size: 0.78rem; color: #6c757d; margin-top: 0.15rem; line-height: 1.3; }

.cp-receipt-email-block {
  margin-top: 0.5rem;
  padding-left: 1.85rem;
  input { max-width: 360px; }
}
.cp-warning-row {
  font-size: 0.8rem;
  color: #d39e00;
  background: #fff9db;
  border: 1px solid #ffec99;
  border-radius: 4px;
  padding: 0.4rem 0.6rem;
  margin-bottom: 0.5rem;
}

.cp-actions {
  display: flex;
  gap: 0.5rem;
  margin-top: 1.5rem;
  padding-top: 1rem;
  border-top: 1px solid #e0e0e0;

  &.cp-actions-center {
    justify-content: center;
    border-top: none;
  }
}

.cp-state-block {
  text-align: center;
  padding: 2rem 1rem;

  .cp-state-icon {
    font-size: 3rem;
    margin-bottom: 1rem;
    &.spin { color: #547fff; }
    &.ok { color: #28a745; }
    &.bad { color: #dc3545; }
    &.mute { color: #6c757d; }
    &.warn { color: #ffc107; }
  }

  .cp-state-amount {
    font-size: 1.6rem;
    font-weight: 600;
    color: #28a745;
    margin: 0.5rem 0;
  }

  // Partial approval: requested / approved / not collected. Left-aligned inside
  // a centred block on purpose — three amounts to compare read as a statement,
  // and the shortfall is the line the operator has to act on.
  .cp-partial-breakdown {
    max-width: 22rem;
    margin: 1rem auto;
    text-align: left;
    font-size: 0.9rem;

    .cp-partial-row {
      display: flex;
      justify-content: space-between;
      padding: 0.35rem 0;
      border-bottom: 1px solid #eef0f4;

      &:last-child { border-bottom: 0; }

      &.cp-partial-row--due {
        font-weight: 600;
        color: #b8860b;
      }
    }
  }
}
</style>
